import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Row, Col, ConfirmationWithReasonModal } from 'components-antd';
import { Options } from 'components';

import { NamesList } from 'pages/Calendar/components';
import { Icons } from 'pages/Calendar/Icons';

import { CalendarEventKey } from 'pages/Calendar/constants';
import {
  getTransactionEffect,
  resetTransactionEffect,
  setTransactionTaskParamsEffect,
} from 'store/effects';
import { link } from 'settings/navigation/link';
import { useManageShowingsList } from 'pages/Workshop/Showings/components/ShowingTabs/useManageShowingsList';
import { AppointmentDetailsModal } from 'pages/Workshop/Showings/components/AppointmentDetailsModal';
import { ApprovalAction } from 'types';

import dayTileStyles from './../DayView/styles.module.scss';

export const DayTileItem = ({
  id,
  transactionId,
  campaignId,
  uuid,
  type,
  title,
  startTime,
  endTime,
  subtitle,
  client,
  agentsOrAssignees,
  className,
  isTasks,
  isProject,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    data,
    showModal,
    isFeedback,
    processData,
    actionConfig,
    processAction,
    onDetailsClose,
    onReasonConfirm,
    pendingApprovers,
    appointmentModal,
    isApprovalRequired,
    redirectToOverview,
    onConfirmationCancel,
  } = useManageShowingsList({ fetchShowingAggregate: null, activeTab: null });

  const openTaskDetailsModal = () => {
    if (!campaignId) dispatch(getTransactionEffect({ id: transactionId }));

    dispatch(
      setTransactionTaskParamsEffect({
        taskId: id,
        transactionId,
        campaignId,
        isTaskDetail: true,
      }),
    );
  };

  const openShowingsModal = () => redirectToOverview({});

  const redirectToOffers = () => {
    history.push({
      pathname: link.toTransactionOffers(transactionId),
      state: {
        offerIterationUUID: uuid,
        triggerOfferDetail: true,
      },
    });
  };

  const getOptionLabel = () => {
    if (type === CalendarEventKey.CLOSINGS || type === CalendarEventKey.EXPIRING_LISTINGS)
      return 'Go to Transaction';
    else if (
      type === CalendarEventKey.PROJECT_KICKOFFS ||
      type === CalendarEventKey.EXPIRING_PROJECTS
    )
      return 'Go to Project';
    else if (type === CalendarEventKey.EXPIRING_OFFERS) return 'View Offer';
    else if (type === CalendarEventKey.TASKS) return 'View Task';
    else if (type === CalendarEventKey.SHOWINGS) return 'View Showing';
    else return '';
  };

  const onOptionClick = () => {
    dispatch(resetTransactionEffect());

    if (type === CalendarEventKey.CLOSINGS || type === CalendarEventKey.EXPIRING_LISTINGS) {
      history.push(link.toTransactionOverview(transactionId));
    } else if (
      type === CalendarEventKey.PROJECT_KICKOFFS ||
      type === CalendarEventKey.EXPIRING_PROJECTS
    )
      history.push(link.toProjectOverview(transactionId));
    else if (type === CalendarEventKey.EXPIRING_OFFERS) redirectToOffers();
    else if (type === CalendarEventKey.TASKS) openTaskDetailsModal();
    else if (type === CalendarEventKey.SHOWINGS) openShowingsModal();
    else return;
  };

  const getAgentColumnLabel = () => {
    if (isProject) return 'Owner';
    if (isTasks) return 'Assignee(s)';

    return 'Agent';
  };

  return (
    <>
      <div className={dayTileStyles.dayTileItem}>
        <Row gutter={24} align="middle">
          <Col lg={4}>
            <div className={dayTileStyles.timeline}>
              <div className={classNames(dayTileStyles.bulletPoint, className)} />
              <span>{startTime ? `${startTime} ${endTime ? `- ${endTime}` : ''}` : 'All day'}</span>
            </div>
          </Col>
          <Col lg={12}>
            <div className={dayTileStyles.descriptionContainer}>
              {type !== CalendarEventKey.TOURS ? (
                <Options
                  title={title}
                  placement="bottom"
                  options={[
                    {
                      label: getOptionLabel(),
                      icon: <Icons variant={Icons.NAVIGATE} />,
                      onClick: onOptionClick,
                    },
                  ]}
                  popoverClassName={dayTileStyles.titleOptionsPopover}
                  titleClassName={classNames(dayTileStyles.title, dayTileStyles.titleHover)}
                />
              ) : (
                <p className={dayTileStyles.title}>{title}</p>
              )}
              <p className={dayTileStyles.subtitle}>{subtitle}</p>
            </div>
          </Col>
          <Col lg={4}>
            {agentsOrAssignees?.length > 0 && (
              <div className={dayTileStyles.clientContainer}>
                <div className={dayTileStyles.clientName}>{getAgentColumnLabel()}:</div>
                <NamesList names={agentsOrAssignees} />
              </div>
            )}
          </Col>
          {!isProject && client?.length > 0 && (
            <Col lg={4}>
              <div className={dayTileStyles.clientContainer}>
                <div className={dayTileStyles.clientName}>Client:</div>
                <NamesList names={client} />
              </div>
            </Col>
          )}
        </Row>
      </div>
      <div className={dayTileStyles.separator} />

      <ConfirmationWithReasonModal
        open={showModal}
        confirmText={<span>Are you sure you want to {actionConfig?.action} this showing?</span>}
        placeholder={`Write a reason for ${
          actionConfig?.action === ApprovalAction.Decline ? 'declining' : 'cancelling'
        }...`}
        okText={actionConfig?.action === ApprovalAction.Cancel ? 'Confirm' : actionConfig?.action}
        showReason={!(actionConfig?.action === ApprovalAction.Approve)}
        onOk={onReasonConfirm}
        onCancel={onConfirmationCancel}
      />

      {data !== null && (
        <AppointmentDetailsModal
          isOpen={appointmentModal}
          onClose={onDetailsClose}
          UUID={uuid}
          isApprovalRequired={isApprovalRequired}
          pendingApprovers={pendingApprovers}
          processAction={processAction}
          isFeedback={isFeedback}
          processData={processData}
        />
      )}
    </>
  );
};
